import { Modal } from './Modal.js';
import { buildableObjects, constructionCost, formatCost } from './schema.js';
import type { AssetLoader } from './AssetLoader.js';
import type { Economy } from './Economy.js';
import type { Category, GameConfig, GameObject } from '../types.js';

interface BuildWindowOptions {
    config: GameConfig;
    assetLoader: AssetLoader;
    economy?: Economy | null;
    isUnlocked?: ((objectId: string) => boolean) | null;
    /** False when the object's category has run out of build slots. */
    hasSlot?: ((objectId: string) => boolean) | null;
    /** Slot usage for a category, or null when it is uncapped. */
    categorySlots?: ((categoryId: string) => { used: number; limit: number } | null) | null;
    onBuild?: ((objectId: string) => void) | null;
}

export class BuildWindow {
    private readonly config: GameConfig;
    private readonly assetLoader: AssetLoader;
    private readonly economy: Economy | null;
    private readonly isUnlocked: ((objectId: string) => boolean) | null;
    private readonly hasSlot: ((objectId: string) => boolean) | null;
    private readonly categorySlots:
        ((categoryId: string) => { used: number; limit: number } | null) | null;
    private readonly onBuild: ((objectId: string) => void) | null;
    private readonly modal: Modal;
    private readonly categories: Category[];
    private readonly rail: HTMLElement;
    private readonly grid: HTMLElement;
    private activeCategory: string | null;

    constructor({
        config,
        assetLoader,
        economy = null,
        isUnlocked = null,
        hasSlot = null,
        categorySlots = null,
        onBuild = null,
    }: BuildWindowOptions) {
        this.config = config;
        this.assetLoader = assetLoader;
        this.economy = economy;
        this.isUnlocked = isUnlocked;
        this.hasSlot = hasSlot;
        this.categorySlots = categorySlots;
        this.onBuild = onBuild;

        this.categories = this.resolveCategories();
        this.activeCategory = this.categories[0]?.id ?? null;

        this.modal = new Modal({
            title: config.ui?.buildTitle ?? 'BUILD IT',
            variant: 'build',
        });

        const layout = document.createElement('div');
        layout.className = 'build-layout';

        this.rail = document.createElement('div');
        this.rail.className = 'build-rail';
        this.rail.setAttribute('role', 'tablist');

        this.grid = document.createElement('div');
        this.grid.className = 'build-grid';

        layout.appendChild(this.rail);
        layout.appendChild(this.grid);
        this.modal.body.appendChild(layout);

        this.renderRail();
        this.renderGrid();
    }

    private resolveCategories(): Category[] {
        const declared = this.config.categories;
        if (Array.isArray(declared) && declared.length) return declared;

        const seen: string[] = [];
        buildableObjects(this.config).forEach((object) => {
            if (object.category && !seen.includes(object.category)) seen.push(object.category);
        });
        if (seen.length) return seen.map((id) => ({ id, label: id }));

        return [{ id: 'all', label: 'All' }];
    }

    private objectsFor(categoryId: string | null): GameObject[] {
        const known = new Set(this.categories.map((c) => c.id));
        const isFirst = this.categories[0]?.id === categoryId;

        return buildableObjects(this.config).filter((object) => {
            if (object.category && known.has(object.category)) {
                return object.category === categoryId;
            }
            return isFirst;
        });
    }

    private renderRail(): void {
        this.rail.innerHTML = '';

        this.categories.forEach((category) => {
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'build-tab';
            tab.setAttribute('role', 'tab');
            const selected = category.id === this.activeCategory;
            tab.setAttribute('aria-selected', String(selected));
            if (selected) tab.classList.add('is-active');

            if (category.icon && this.assetLoader.getImage(category.icon)) {
                const img = document.createElement('img');
                img.className = 'build-tab__icon';
                img.src = this.assetLoader.resolve(category.icon);
                img.alt = '';
                tab.appendChild(img);
            }

            const label = document.createElement('span');
            label.textContent = category.label || category.id;
            tab.appendChild(label);

            // A category with a slot cap reads as broken once it fills up —
            // the cost is affordable but nothing can be built. Showing the
            // count makes the limit visible before the player tries.
            const slots = this.categorySlots?.(category.id) ?? null;
            if (slots) {
                const count = document.createElement('span');
                count.className = 'build-tab__slots';
                count.textContent = `${slots.used}/${slots.limit}`;
                if (slots.used >= slots.limit) count.classList.add('is-full');
                tab.appendChild(count);
            }

            tab.addEventListener('click', () => {
                this.activeCategory = category.id;
                this.renderRail();
                this.renderGrid();
            });

            this.rail.appendChild(tab);
        });
    }

    private renderGrid(): void {
        this.grid.innerHTML = '';
        const objects = this.objectsFor(this.activeCategory);

        if (!objects.length) {
            const empty = document.createElement('p');
            empty.className = 'build-empty';
            empty.textContent = 'Nothing to build here yet.';
            this.grid.appendChild(empty);
            return;
        }

        objects.forEach((object) => this.grid.appendChild(this.renderCard(object)));
    }

    private renderCard(object: GameObject): HTMLElement {
        const card = document.createElement('div');
        const locked = this.isUnlocked ? !this.isUnlocked(object.id) : object.locked === true;
        card.className = locked ? 'build-card is-locked' : 'build-card';

        const label = document.createElement('span');
        label.className = 'build-card__label';
        label.textContent = object.label || object.id;
        card.appendChild(label);

        const art = document.createElement('div');
        art.className = 'build-card__art';
        if (this.assetLoader.getImage(object.sprite)) {
            const img = document.createElement('img');
            img.src = this.assetLoader.resolve(object.sprite!);
            img.alt = '';
            art.appendChild(img);
        } else {
            const swatch = document.createElement('div');
            swatch.className = 'build-card__swatch';
            swatch.style.background = object.color ?? '#c97f00';
            art.appendChild(swatch);
        }
        card.appendChild(art);

        const cost = formatCost(this.config, constructionCost(object, 1));
        if (cost) {
            const costLine = document.createElement('span');
            costLine.className = 'build-card__cost';
            costLine.textContent = cost;
            card.appendChild(costLine);
        }

        card.appendChild(locked ? this.renderLock() : this.renderBuild(object));
        return card;
    }

    /** The padlock is themed art applied by CSS, so this carries no glyph. */
    private renderLock(): HTMLElement {
        const lock = document.createElement('div');
        lock.className = 'build-card__lock';
        lock.setAttribute('role', 'img');
        lock.setAttribute('aria-label', 'Locked');
        return lock;
    }

    private renderBuild(object: GameObject): HTMLElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'build-card__build';
        button.textContent = 'BUILD';

        const cost = constructionCost(object, 1);
        if (this.economy && !this.economy.canAfford(cost)) {
            button.disabled = true;
            button.title = 'Not enough resources';
        } else if (this.hasSlot && !this.hasSlot(object.id)) {
            // The engine refuses this placement anyway; without saying so here
            // the button just looks broken to a player who can afford it.
            // The label carries the reason too, since a title only surfaces
            // on hover and the player is looking at the button, not past it.
            button.disabled = true;
            button.textContent = 'FULL';
            button.title = 'No free slot in this category';
        }

        button.addEventListener('click', () => {
            this.modal.close();
            this.onBuild?.(object.id);
        });
        return button;
    }

    open(): void {
        // The rail carries live slot counts, so it has to be rebuilt too —
        // rendering only the grid leaves a stale "2/3" on a full category.
        this.renderRail();
        this.renderGrid();
        this.modal.open();
    }

    close(): void {
        this.modal.close();
    }

    toggle(): void {
        if (this.modal.isOpen) this.modal.close();
        else this.open();
    }
}
